import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import type { DashboardJob } from '@/hooks/useDashboardData';

interface UnassignedJobsAlertProps {
  /** Upcoming + future jobs already loaded by the dashboard (this week → +90d). */
  jobs: DashboardJob[];
}

function isUnassignedActionableJob(job: DashboardJob): boolean {
  return job.team_member_id == null && ['scheduled', 'confirmed'].includes(job.status);
}

function jobSortKey(job: DashboardJob): string {
  return `${job.scheduled_date} ${job.start_time}`;
}

function formatUnassignedLabel(job: DashboardJob): string {
  const title = job.title?.trim() || 'Job';
  const date = format(parseISO(job.scheduled_date), 'EEE, MMM d');
  return `Unassigned Job (${title} - ${date})`;
}

/**
 * Dashboard alert shown only to owners/admins (any non-cleaner staff role).
 *
 * By default it renders just a compact warning icon button — keeping the
 * dashboard uncluttered. When upcoming/future jobs still have no team member
 * assigned, a count sits on the icon. Clicking it opens a popover listing those
 * jobs; each item deep-links to the schedule Team view for that date with the
 * Unassigned filter active so the owner can assign a cleaner immediately.
 * Renders nothing for cleaners and nothing when every
 * upcoming job is assigned.
 */
export function UnassignedJobsAlert({ jobs }: UnassignedJobsAlertProps) {
  const { staffRole } = useAuth();
  const isAdminViewer = staffRole != null && staffRole !== 'cleaner';

  const unassignedJobs = useMemo(
    () =>
      jobs
        .filter(isUnassignedActionableJob)
        .sort((a, b) => jobSortKey(a).localeCompare(jobSortKey(b))),
    [jobs]
  );

  if (!isAdminViewer || unassignedJobs.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          data-testid="dashboard-unassigned-jobs-alert-trigger"
          aria-label={`Unassigned jobs: ${unassignedJobs.length} need a cleaner assigned`}
          className="relative h-9 w-9 rounded-lg border border-border bg-card text-foreground shadow-[var(--adm-shadow-xs)] hover:bg-muted hover:text-foreground [&_svg]:size-4"
        >
          <AlertTriangle className="h-4 w-4" />
          <span
            data-testid="dashboard-unassigned-jobs-alert-count"
            className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
          >
            {unassignedJobs.length}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-3"
        data-testid="dashboard-unassigned-jobs-alert"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
          <span className="text-sm font-medium">Unassigned jobs</span>
        </div>
        <ul className="mt-2 space-y-1">
          {unassignedJobs.map((job) => (
            <li key={job.id}>
              <Link
                to={`/admin/schedule?view=team&date=${job.scheduled_date}&job=${job.id}&filter=unassigned`}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-primary/10"
              >
                <span className="flex-1 truncate font-medium text-foreground">
                  {formatUnassignedLabel(job)}
                </span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </Link>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
